import { subHours, format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import Chip from '../common/chip'
import Dialog from '../common/dialog'
import { numberWithCommas } from '../../utils'
import SwapIcon from '../../assets/icons/swap-icon.svg'
import { requestAccessToken } from '../../api/auth.api'
import AccountIcon from '../../assets/icons/account-icon.svg'
import CalendarIcon from '../../assets/icons/calendar-icon.svg'
import { CustomerCompanyFunding, FormError } from '../../types'
import DocumentIcon from '../../assets/icons/document-icon.svg'
import MoneyGreyIcon from '../../assets/icons/money-grey-icon.svg'
import { approveCustomerCompanyFunding } from '../../api/customers.api'
import { AuthContext, NotificationsContext, UserContext } from '../../contexts'
import { NegotiationType, NegotiationTypeText } from '../customer-companies-management/lib'

export const CustomerCompanyFundingDetailsModal = ({
  handleSetModalOpenState,
  selectedCustomerCompanyFunding,
  handleOpenRevertCustomerCompanyFundingModal,
  handleOpenRejectCustomerCompanyFundingModal
}: {
  handleSetModalOpenState: (openState: boolean) => void
  selectedCustomerCompanyFunding?: CustomerCompanyFunding
  handleOpenRejectCustomerCompanyFundingModal?: () => void
  handleOpenRevertCustomerCompanyFundingModal?: () => void
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t } = useTranslation(['customer-company-details-management', 'common'])

  const [errors, setErrors] = useState<FormError>({})
  const [dialogIsLoading, setDialogIsLoading] = useState<boolean>(false)
  const [showCustomerCompanyRow, setShowCustomerCompanyRow] = useState(false)

  useEffect(() => {
    if (location.pathname.indexOf('customer-compan') === -1) {
      setShowCustomerCompanyRow(true)
    }
  }, [location])

  const handleApproveCustomerCompanyFunding = async () => {
    setErrors({})

    try {
      setDialogIsLoading(true)
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await approveCustomerCompanyFunding(String(selectedCustomerCompanyFunding?._id), selectedCountry, accessTokenData.data.accessToken)
      handleSetModalOpenState(false)
      updateNewNotificationStatus?.(true, 'approve-funding-allocation-success')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogIsLoading(false)
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <Dialog
      // hideCloseOption={selectedCustomerCompanyFunding?.status === 'approved'}
      dialogIsLoading={dialogIsLoading}
      dialogTitle={t('fundingDetailsModal.title')}
      handleDialogAction={() => {
        if (selectedCustomerCompanyFunding?.status === 'approved') {
          handleSetModalOpenState(false)
          return
        }

        handleApproveCustomerCompanyFunding()
      }}
      handleCloseOptionAction={() => {
        if (selectedCustomerCompanyFunding?.status === 'processing') {
          handleOpenRejectCustomerCompanyFundingModal?.()
          return
        }

        if (selectedCustomerCompanyFunding?.status === 'approved') {
          handleOpenRevertCustomerCompanyFundingModal?.()
          return
        }

        handleSetModalOpenState(false)
      }}
      actionButtonText={selectedCustomerCompanyFunding?.status === 'processing' ? t('fundingDetailsModal.actionButtonAssignText') : t('fundingDetailsModal.actionButtonAcceptText')}
      closeOptionWarning={selectedCustomerCompanyFunding?.status === 'processing' || selectedCustomerCompanyFunding?.status === 'approved' ? true : false}
      handleDialogOpenState={(openState: boolean) => handleSetModalOpenState(openState)}
      closeButtonText={
        selectedCustomerCompanyFunding?.status === 'processing'
          ? t('fundingDetailsModal.actionButtonRejectText')
          : selectedCustomerCompanyFunding?.status === 'approved'
          ? t('fundingDetailsModal.actionButtonRevertText')
          : undefined
      }
      hideActionsButtons={userSession?.role?.includes('-client') || (userSession?.role.indexOf('-client') === -1 && selectedCustomerCompanyFunding?.status !== 'processing')}
    >
      <div className="customer-company-fundings-details-modal-content-container">
        <div className="modal-info-row">
          <div className="left-info-data-container">
            <img
              src={AccountIcon}
              alt="account-icon"
            />
            <div className="info-data">{`${t('fundingDetailsModal.subtitles.businessName')}:`}</div>
          </div>
          <div className="right-info-data-container">
            <div className="info-data">{selectedCustomerCompanyFunding?.assignedTo.customerCompanyBusinessName.toUpperCase()}</div>
            {showCustomerCompanyRow && (
              <div
                className="info-data"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/customer-company-details/${selectedCustomerCompanyFunding?.assignedTo.customerCompanyId}`)}
              >
                <strong style={{ color: '#107569' }}>{`${t('fundingDetailsModal.buttongGoToText')}`}</strong>
              </div>
            )}
          </div>
        </div>
        {selectedCustomerCompanyFunding?.status === 'rejected' && <div className="modal-info-row rejected-reason">{selectedCustomerCompanyFunding.rejectedBy?.reason}</div>}
        {selectedCustomerCompanyFunding?.status === 'approved' && (
          <div className="modal-info-row">
            <div className="left-info-data-container">
              <img
                src={CalendarIcon}
                alt="calendar-icon"
              />
              <div className="info-data">{`${t('fundingDetailsModal.subtitles.assignedAt')}:`}</div>
            </div>
            <div className="right-info-data-container">
              <div className="info-data">{format(new Date(selectedCustomerCompanyFunding?.assignedBy?.assignedAt ?? 0), 'dd/MM/yyyy |HH:mm:ss')}</div>
            </div>
          </div>
        )}
        {selectedCustomerCompanyFunding?.status === 'rejected' && (
          <div className="modal-info-row">
            <div className="left-info-data-container">
              <img
                src={CalendarIcon}
                alt="calendar-icon"
              />
              <div className="info-data">{`${t('fundingDetailsModal.subtitles.rejectedAt')}:`}</div>
            </div>
            <div className="right-info-data-container">
              <div className="info-data">{format(new Date(selectedCustomerCompanyFunding?.rejectedBy?.rejectedAt ?? 0), 'dd/MM/yyyy')}</div>
            </div>
          </div>
        )}
        <div className="modal-info-row">
          <div className="left-info-data-container">
            <img
              src={SwapIcon}
              alt="swap-icon"
            />
            <div className="info-data">{`${t('fundingDetailsModal.subtitles.currencyTradingInformation')}:`}</div>
          </div>
          <div className="right-info-data-container">
            <div
              className="info-data"
              style={{ width: '100%', justifyContent: 'space-between' }}
            >
              <strong style={{ color: '#107569' }}>{`${t('fundingDetailsModal.subtitles.quantity')}:`}</strong>
              <div>{`USD$ ${numberWithCommas(Number(selectedCustomerCompanyFunding?.fundingPurchaseQuantity))}`}</div>
            </div>
            <div
              className="info-data"
              style={{ width: '100%', justifyContent: 'space-between' }}
            >
              <strong style={{ color: '#107569' }}>{`${t('fundingDetailsModal.subtitles.purchaseRate')}:`}</strong>
              <div style={{ textAlign: 'right' }}>{`$ ${numberWithCommas(Number(selectedCustomerCompanyFunding?.fundingPurchaseRate))} USD/${
                selectedCustomerCompanyFunding?.expectedProfitCurrency ? selectedCustomerCompanyFunding?.expectedProfitCurrency?.toUpperCase() : 'COP'
              }`}</div>
            </div>
            <div
              className="info-data"
              style={{ width: '100%', justifyContent: 'space-between' }}
            >
              <strong style={{ color: '#107569' }}>{`${t('fundingDetailsModal.subtitles.negotiationType')}:`}</strong>
              <div>{NegotiationTypeText[selectedCustomerCompanyFunding?.negotiationType as NegotiationType]}</div>
            </div>
            {(selectedCustomerCompanyFunding?.negotiationType as NegotiationType) === NegotiationType.ON_SPOT && (
              <div
                className="info-data"
                style={{ width: '100%', justifyContent: 'space-between' }}
              >
                <strong style={{ color: '#107569' }}>{`${t('fundingDetailsModal.subtitles.negotiationAdditionalPercentage')}:`}</strong>
                <div>{`${numberWithCommas(Number(selectedCustomerCompanyFunding?.negotiationAdditionalPercentage))}%`}</div>
              </div>
            )}
            <div
              className="info-data"
              style={{ width: '100%', justifyContent: 'space-between' }}
            >
              <strong style={{ color: '#107569' }}>{`${t('fundingDetailsModal.subtitles.date')}:`}</strong>
              {selectedCustomerCompanyFunding?.assignedBy?.assignedAt ? (
                <div>{format(subHours(new Date(selectedCustomerCompanyFunding?.assignedBy?.assignedAt ?? 0), 0.5), 'dd/MM/yyyy |HH:mm:ss')}</div>
              ) : (
                <div>{format(subHours(new Date(selectedCustomerCompanyFunding?.createdAt ?? 0), 0.5), 'dd/MM/yyyy |HH:mm:ss')}</div>
              )}
            </div>
          </div>
        </div>
        <div
          className="modal-info-row"
          style={{ borderBottom: 'none' }}
        >
          <div className="left-info-data-container">
            <Chip
              addStyle={{ maxWidth: 'none' }}
              status={String(selectedCustomerCompanyFunding?.status)}
              label={
                selectedCustomerCompanyFunding?.status === 'processing'
                  ? t('fundingStatus.processing')
                  : selectedCustomerCompanyFunding?.status === 'pending-credit'
                  ? t('fundingStatus.pending-credit')
                  : selectedCustomerCompanyFunding?.status === 'rejected'
                  ? t('fundingStatus.rejected')
                  : t('fundingStatus.approved')
              }
            />
          </div>
          <div className="right-info-data-container">
            <div className="info-data">
              <img
                src={AccountIcon}
                alt="account-icon"
              />
              <div className="info-data">
                {selectedCustomerCompanyFunding?.status === 'processing'
                  ? '---'
                  : selectedCustomerCompanyFunding?.status === 'rejected'
                  ? selectedCustomerCompanyFunding?.rejectedBy?.systemUserName.toUpperCase()
                  : selectedCustomerCompanyFunding?.assignedBy?.systemUserName.toUpperCase()}
              </div>
            </div>
          </div>
        </div>
        <div
          className="modal-info-row allocation-info-row"
          style={selectedCustomerCompanyFunding?.description ? { marginBottom: '12px' } : {}}
        >
          <div className="left-info-data-container">
            <img
              src={MoneyGreyIcon}
              alt="money-grey-icon"
            />
            <div className="info-data">{`${t('fundingDetailsModal.subtitles.allocationInfo')}:`}</div>
          </div>
          <div className="right-info-data-container">
            <div className="info-data">
              <strong>
                {selectedCustomerCompanyFunding?.fundingSaleRate
                  ? `$ ${numberWithCommas(
                      Math.round(Number(selectedCustomerCompanyFunding?.fundingSaleQuantity) * Number(selectedCustomerCompanyFunding?.fundingSaleRate))
                    )} ${selectedCustomerCompanyFunding?.expectedProfitCurrency.toUpperCase()}`
                  : `$ ${numberWithCommas(Math.round(Number(selectedCustomerCompanyFunding?.purchaseNetValueResult)))} ${selectedCustomerCompanyFunding?.expectedProfitCurrency.toUpperCase()}`}
              </strong>
            </div>
            <div className="info-data">
              {selectedCustomerCompanyFunding?.fundingSaleRate
                ? `USD$ ${numberWithCommas(Number(selectedCustomerCompanyFunding?.fundingSaleQuantity))}`
                : `USD$ ${numberWithCommas(Number(selectedCustomerCompanyFunding?.fundingPurchaseQuantity))}`}
            </div>
          </div>
        </div>
        {selectedCustomerCompanyFunding?.description && (
          <div
            className="modal-info-row"
            style={{ borderTop: '1px solid #E5E5E5', borderBottom: 'none' }}
          >
            <div className="left-info-data-container">
              <img
                src={DocumentIcon}
                alt="document-icon"
              />
              <div className="info-data">{`${t('fundingDetailsModal.subtitles.description')}:`}</div>
            </div>
            <div className="right-info-data-container">
              <div className="info-data">{selectedCustomerCompanyFunding?.description}</div>
            </div>
          </div>
        )}
      </div>
      {errors?.generalError && (
        <div
          className="error-message"
          style={{ margin: '20px 0px 0px' }}
        >
          {errors?.generalError}
        </div>
      )}
    </Dialog>
  )
}
